/**
 * AiHint — subdued info card matching the prototype's `.ai-hint` slot.
 *
 * Used inside composer sections (Block 1 "Customer & billing context"
 * and Block 4 "Send settings") to reserve the visual space where a
 * Flint suggestion would appear once the AI vertical ships in V1.x.
 *
 * V1.0.x renders only `body` text — no `onApply` action button — so
 * the slot is honest about its deferred state instead of faking data.
 * When the AI vertical lands, the caller passes a real suggestion +
 * an apply handler and the card lights up.
 *
 * @see prototype/sales/invoice-new.html — `.ai-hint` blocks in sections 1 + 4.
 * @see docs/spec/11-new-invoice-screen-spec.md §5.2 — derived defaults.
 */
import { Sparkles } from "lucide-react";

export interface AiHintProps {
  /**
   * The hint text. Markdown-style emphasis isn't supported — pass
   * pre-shaped React nodes if you need bold spans (e.g. `<strong>Net
   * 15</strong>`).
   */
  body: React.ReactNode;
  /**
   * Optional "Apply" action. When omitted (the V1.0.x default) the
   * card is read-only — the AI vertical wires this up later.
   */
  onApply?: () => void;
  /** Visual variant. `'deferred'` dims the card to signal V1.x. */
  variant?: "live" | "deferred";
}

export function AiHint({ body, onApply, variant = "deferred" }: AiHintProps) {
  const dim = variant === "deferred";
  return (
    <div
      className={[
        "flex items-start gap-2.5 rounded-md border px-3 py-2.5 text-sm",
        dim ? "border-line bg-raised text-muted" : "border-sky-100 bg-sky-50 text-sky-900",
      ].join(" ")}
    >
      <Sparkles
        size={14}
        className={dim ? "mt-0.5 shrink-0 text-muted" : "mt-0.5 shrink-0 text-sky-600"}
        aria-hidden
      />
      <div className="min-w-0 flex-1 leading-snug">{body}</div>
      {onApply ? (
        <button
          type="button"
          onClick={onApply}
          className="shrink-0 rounded-sm border border-line bg-paper px-2 py-1 text-xs font-medium text-ink-2 hover:bg-bg"
        >
          Apply
        </button>
      ) : null}
    </div>
  );
}

/**
 * AiTrailingButton — small indigo-themed button slot for section
 * headings (e.g. "Suggest customer", "Lookup HSN"). Matches the
 * prototype's `.btn-ai` shape:
 *   30px tall · indigo-50 bg · indigo-100 border · indigo-700 text.
 *
 * Always disabled in V1.0.x — the AI vertical lands in V1.x and the
 * action handler wires up then.
 */
export function AiTrailingButton({
  label,
  title,
  onClick,
}: {
  label: string;
  /** Tooltip explaining why the button is currently inert. */
  title: string;
  onClick?: () => void;
}) {
  const disabled = !onClick;
  return (
    <button
      type="button"
      disabled={disabled}
      title={title}
      onClick={onClick}
      className={[
        "inline-flex h-[30px] items-center gap-1.5 rounded-sm border px-2.5 text-xs font-medium",
        "border-indigo-200 bg-indigo-50 text-indigo-700",
        disabled ? "cursor-not-allowed opacity-70" : "hover:border-indigo-400 hover:bg-indigo-100",
      ].join(" ")}
    >
      <Sparkles size={12} aria-hidden />
      <span>{label}</span>
    </button>
  );
}
